// Cover Page & Project Info tab component
import React from 'react';
import { useReports } from '../ReportsProvider';
import { A4Page } from '../components/A4Page';

const CoverPageTab = () => {
  const { 
    reportData, 
    setReportData, 
    reportSections, 
    updateReportSection,
    selectedPeriod, 
    setSelectedPeriod,
    selectedSite, 
    setSelectedSite,
    sites, 
    organizationSettings
  } = useReports();

  // Helper function to format date range
  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return 'Date range not specified';
    
    const start = new Date(startDate).toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
    const end = new Date(endDate).toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
    
    return `${start} - ${end}`;
  };

  // Get selected site data
  const selectedSiteData = selectedSite ? sites.find(site => site.id === selectedSite) : null;
  const reportDate = new Date().toLocaleDateString('en-GB', { 
    day: '2-digit', 
    month: 'long', 
    year: 'numeric' 
  });

  const renderCoverPageTab = () => {
    return (
      <A4Page pageNumber={1} showPageNumber={false}>
        <div style={{ 
          fontFamily: 'system-ui, -apple-system, sans-serif',
          width: '190mm', // Fixed A4 content width
          height: '277mm' // Fixed A4 content height
        }}>
        
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-8 relative overflow-hidden rounded-t-lg">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full bg-repeat opacity-20" style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
            }}></div>
          </div>
          
          <div className="relative z-10">
            {/* Report Type Banner - Direct in header */}
            <h2 className="text-2xl font-bold mb-1 tracking-tight text-white">
              CONSTRUCTION PROGRESS REPORT
            </h2>
            <p className="text-sm font-medium text-white">
              Comprehensive Project Documentation & Analysis
            </p>
          </div>
        </div>

        {/* Main Content Section */}
        <div style={{ 
          backgroundColor: '#f9fafb', 
          borderBottomLeftRadius: '8px', 
          borderBottomRightRadius: '8px' 
        }}>
          
          {/* Report Title Section - EDITABLE - FIXED WIDTH */}
          <div style={{ 
            width: '190mm',
            backgroundColor: 'white', 
            borderRadius: '12px', 
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', 
            padding: '6mm', 
            marginTop: '8mm',
            marginBottom: '6mm', 
            borderLeft: '4px solid #2563eb',
            boxSizing: 'border-box'
          }}>
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                <span className="text-blue-600 text-xs">📋</span>
              </div>
              Report Details
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4mm' }}>
              {/* Editable Report Title */}
              <div>
                <label className="block text-sm font-medium text-blue-600 mb-2 uppercase">Title</label>
                <input
                  type="text"
                  value={reportData.title}
                  onChange={(e) => setReportData(prev => ({ ...prev, title: e.target.value }))}
                  style={{
                    width: '100%',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#1e3a8a',
                    border: '2px dashed #93c5fd',
                    borderRadius: '8px',
                    padding: '4mm',
                    backgroundColor: '#eff6ff'
                  }}
                  placeholder="e.g., Construction Progress Report - December 2024"
                />
              </div>
              
              {/* Report Period and Generated Date */}
              <div style={{ display: 'flex', gap: '8mm' }}>
                <div style={{ flex: 1 }}>
                  <label className="block text-sm font-medium text-blue-600 mb-2 uppercase">Period</label>
                  <div style={{ display: 'flex', gap: '2mm' }}>
                    <input
                      type="date"
                      value={selectedPeriod.startDate}
                      onChange={(e) => setSelectedPeriod(prev => ({ ...prev, startDate: e.target.value }))}
                      style={{
                        width: '42mm',
                        padding: '3mm',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        fontSize: '13px'
                      }}
                    />
                    <input
                      type="date"
                      value={selectedPeriod.endDate}
                      onChange={(e) => setSelectedPeriod(prev => ({ ...prev, endDate: e.target.value }))}
                      style={{
                        width: '42mm',
                        padding: '3mm',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="block text-sm font-medium text-blue-600 mb-2 uppercase">Published</label>
                  <div style={{
                    padding: '3mm',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    fontSize: '13px'
                  }}>
                    {reportDate}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Two Column Layout - Equal 90mm widths */}
          <div style={{ display: 'flex', gap: '10mm', marginBottom: '6mm' }}>
            
            {/* Project Information Card - 90mm width */}
            <div style={{ 
              width: '90mm',
              minWidth: '90mm',
              maxWidth: '90mm',
              backgroundColor: 'white', 
              borderRadius: '12px', 
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', 
              padding: '6mm', 
              borderLeft: '4px solid #2563eb',
              boxSizing: 'border-box'
            }}>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                  <span className="text-blue-600 text-xs">🏗️</span>
                </div>
                Project Information
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4mm' }}>
                <div>
                  <label className="block text-sm font-semibold text-blue-600 uppercase tracking-wide mb-2">Site</label>
                  <select
                    value={selectedSite}
                    onChange={(e) => setSelectedSite(e.target.value)}
                    style={{
                      width: '78mm',
                      padding: '3mm',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      fontSize: '13px'
                    }}
                    required
                  >
                    <option value="">Select a site...</option>
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>{site.site_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide">Location</span>
                  <div style={{
                    width: '78mm',
                    padding: '3mm',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    fontSize: '13px',
                    color: '#1f2937',
                    fontWeight: '500',
                    marginTop: '2mm'
                  }}>
                    {selectedSiteData?.address || 'Select a site to view location'}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-blue-600 uppercase tracking-wide mb-2">Project Status</label>
                  <select
                    value={reportSections.coverPage?.projectStatus || 'In Progress'}
                    onChange={(e) => updateReportSection('coverPage', 'projectStatus', e.target.value)}
                    style={{
                      width: '78mm',
                      padding: '3mm',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      fontSize: '13px'
                    }}
                  >
                    <option value="Not Started">Not Started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
              </div>
            </div>
              
            {/* Stakeholder Information Card - 90mm width */}
            <div style={{ 
              width: '90mm',
              minWidth: '90mm',
              maxWidth: '90mm',
              backgroundColor: 'white', 
              borderRadius: '12px', 
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', 
              padding: '6mm', 
              borderLeft: '4px solid #2563eb',
              boxSizing: 'border-box'
            }}>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                  <span className="text-blue-600 text-xs">👥</span>
                </div>
                Key Stakeholders
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4mm' }}>
                {/* Client Information */}
                <div>
                  <h4 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-2">Client</h4>
                  <p style={{ 
                    padding: '3mm',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    fontSize: '13px',
                    color: '#1f2937',
                    fontWeight: '500',
                    wordWrap: 'break-word',
                    overflow: 'hidden',
                    margin: 0
                  }}>
                      {selectedSiteData?.client?.company_name || `${selectedSiteData?.client?.first_name || ''} ${selectedSiteData?.client?.last_name || ''}`.trim() || 'Select a site to view client'}
                    </p>
                    {selectedSiteData?.client?.phone && (
                      <p className="text-sm text-gray-600" style={{ 
                        wordWrap: 'break-word',
                        overflow: 'hidden'
                      }}>{selectedSiteData.client.phone}</p>
                    )}
                </div>

                {/* Contractor Information */}
                <div>
                  <h4 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-2">Contractor</h4>
                  <p style={{ 
                    padding: '3mm',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    fontSize: '13px',
                    color: '#1f2937',
                    fontWeight: '500',
                    wordWrap: 'break-word',
                    overflow: 'hidden',
                    margin: 0
                  }}>
                    {organizationSettings?.companyName || 'Company Name Not Set'}
                  </p>
                  {organizationSettings?.companyEmail && (
                    <p className="text-sm text-gray-600 mt-1" style={{ 
                      wordWrap: 'break-word',
                      overflow: 'hidden'
                    }}>
                      {organizationSettings.companyEmail}
                    </p>
                  )}
                  {organizationSettings?.companyPhone && (
                    <p className="text-sm text-gray-600" style={{ 
                      wordWrap: 'break-word',
                      overflow: 'hidden'
                    }}>
                      {organizationSettings.companyPhone}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Report Author & Certification Section - EDITABLE - FIXED WIDTH */}
          <div style={{ 
            width: '190mm',
            backgroundColor: 'white', 
            borderRadius: '12px', 
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', 
            padding: '6mm', 
            borderLeft: '4px solid #2563eb',
            boxSizing: 'border-box'
          }}>
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                <span className="text-blue-600 text-xs">✍️</span>
              </div>
              Report Certification
            </h3>
            
            <div style={{ display: 'flex', gap: '6mm' }}>
              <div style={{ width: '86mm' }}>
                <label className="block text-sm font-medium text-blue-600 mb-2 uppercase">Author</label>
                <input
                  type="text"
                  value={reportSections?.coverPage?.reportAuthor || ''}
                  onChange={(e) => updateReportSection('coverPage', 'reportAuthor', e.target.value)}
                  style={{
                    width: '86mm',
                    padding: '3mm',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    fontSize: '13px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{ width: '86mm' }}>
                <label className="block text-sm font-medium text-blue-600 mb-2 uppercase">Signature</label>
                <input
                  type="text"
                  value={reportSections?.coverPage?.signature || ''}
                  onChange={(e) => updateReportSection('coverPage', 'signature', e.target.value)}
                  style={{
                    width: '86mm',
                    padding: '3mm',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    fontSize: '13px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        </div>
        
        {/* Footer Section with matching gradient - pulled upwards */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-3 relative overflow-hidden rounded-b-lg" style={{ marginTop: '-10mm' }}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full bg-repeat opacity-20" style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
            }}></div>
          </div>
        </div>
        
        {/* Bottom margin spacer */}
        <div style={{ marginBottom: '5px' }}></div>
      </A4Page>
    );
  };
  
  return renderCoverPageTab();
};

export default CoverPageTab;